import { useState, useEffect, useRef, useCallback } from "react";
import useStatusParam from "../../hooks/useStatusParam";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import ConfirmDialog from "../../app-components/ui/ConfirmDialog";
import DateFilter from "../../app-components/ui/DateFilter";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import ColumnMenu, {
  ColumnDef,
  useColumnVisibility,
} from "../../app-components/ui/ColumnMenu";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination, { PER_PAGE_OPTIONS } from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { formatDate, getErrorMessage } from "../../utils";
import notify from "../../utils/notify";
import RequestRowActions from "./components/RequestRowActions";
import { downloadCsvExport } from "../../utils/exportCsv";
import ExportButton from "../../app-components/ui/ExportButton";
import { APP_STORAGE } from "../../api-services/Process";

const TABS = [
  { value: "", label: "All requests" },
  { value: "pending", label: "Pending" },
  { value: "closed", label: "Closed" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

// Optional columns the admin can show or hide. Full Name and Actions are always shown.
const COLUMNS: ColumnDef[] = [
  { key: "media", label: "Caption" },
  { key: "username", label: "Username" },
  { key: "gender", label: "Gender", defaultVisible: false },
  { key: "interest", label: "Interest" },
  { key: "text", label: "Prespecified Text", defaultVisible: false },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "city", label: "City", defaultVisible: false },
  { key: "subscription", label: "Subscription" },
  { key: "status", label: "Status" },
  { key: "created", label: "Created At" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

export interface ConnectionRequestType {
  id: number;
  uuid: string;
  user_id: number;
  prespecified_text_id: number;
  expected_response: string;
  status: string;
  interest_id: number;
  caption_image: string | null;
  city_id: number;
  state_id: number;
  country_id: number;
  responders_group: string | null;
  created_at: string;
  updated_at: string;
  country: {
    id: number;
    name: string;
    iso3: string;
    numeric_code: string;
    iso2: string;
    phonecode: string;
    capital: string;
    currency: string;
    currency_name: string;
    currency_symbol: string;
    tld: string;
    native: string;
    region: string;
    region_id: number;
    subregion: string;
    subregion_id: number;
    nationality: string;
    timezones: string;
    translations: string;
    latitude: string;
    longitude: string;
    emoji: string;
    emojiU: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  state: {
    id: number;
    name: string;
    country_id: number;
    country_code: string;
    fips_code: string;
    iso2: string;
    type: string | null;
    latitude: string;
    longitude: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  city: {
    id: number;
    name: string;
    state_id: number;
    state_code: string;
    country_id: number;
    country_code: string;
    latitude: string;
    longitude: string;
    created_at: string;
    updated_at: string;
    flag: number;
    wikiDataId: string;
  };
  interest: {
    id: number;
    interest: string;
    created_at: string | null;
    updated_at: string | null;
  };
  prespecified_text: {
    id: number;
    content: string;
    created_at: string | null;
    updated_at: string | null;
  };
  user: {
    id: number;
    full_name: string;
    email: string;
    email_verified_at: string;
    is_email_verified: string;
    is_phone_verified: string;
    username: string;
    DOB: string;
    gender: string;
    country_id: number;
    state_id: number;
    city_id: number;
    area: string;
    phone_number: string;
    phone_verified_at: string;
    instagram_id: string | null;
    snapchat_id: string | null;
    twitter_x_id: string | null;
    profile_pix_link: string;
    verification_code: string | null;
    bio: string | null;
    is_verified: number;
    badge: string | null;
    interest: string | null;
    is_private: number;
    is_email_notification: number;
    created_at: string;
    updated_at: string;
    last_login: string | null;
    deleted_at: string | null;
    status: string | null;
    active_subscription: {
      id: number;
      user_id: number;
      subscription_plan: string;
      subscription_plan_id: number | null;
      subscription_plan_duration: number;
      connection_request_uuid: string | null;
      status: string;
      start_date: string;
      end_date: string;
      created_at: string;
      updated_at: string;
    };
  };
}

// Inline SVG placeholder (grey media icon) — works offline, no broken icon
const PLACEHOLDER_MEDIA =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <rect width="40" height="40" rx="20" fill="#E4E1E1"/>
      <path d="M13 27l5-6 3 4 4-5 5 7H13z" fill="#B0AEAE"/>
      <circle cx="16" cy="15" r="3" fill="#B0AEAE"/>
    </svg>`
  );

const isVideo = (filePath: string): boolean => {
  const videoExtensions = [".mp4", ".mov", ".webm", ".ogg"];
  return videoExtensions.some((ext) => filePath.toLowerCase().endsWith(ext));
};

const buildUrl = (path: string | null, isGallery: boolean): string => {
  if (!path) return "";
  const galleryFile = isGallery ? "gallery_pictures/" : "";
  return `${APP_STORAGE}${galleryFile}${path}`;
};

// Media cell with graceful fallback to placeholder (null value OR load failure)
const MediaCell = ({
  filePath,
  isGallery,
}: {
  filePath: string | null;
  isGallery: boolean;
}) => {
  const initialUrl = buildUrl(filePath, isGallery);
  const [src, setSrc] = useState<string>(initialUrl || PLACEHOLDER_MEDIA);
  const [isBroken, setIsBroken] = useState<boolean>(!initialUrl);

  // Keep in sync when the underlying value changes (e.g. pagination)
  useEffect(() => {
    const url = buildUrl(filePath, isGallery);
    setSrc(url || PLACEHOLDER_MEDIA);
    setIsBroken(!url);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, isGallery]);

  if (!filePath || isBroken) {
    return (
      <img
        src={PLACEHOLDER_MEDIA}
        alt="no media"
        className="w-10 h-10 object-cover rounded-full shadow-sm"
      />
    );
  }

  const fullUrl = buildUrl(filePath, isGallery);

  return isVideo(filePath) ? (
    <video controls className="w-16 h-10 object-cover rounded shadow-sm">
      <source src={fullUrl} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  ) : (
    <img
      src={src}
      alt="media"
      onError={() => {
        setIsBroken(true);
        setSrc(PLACEHOLDER_MEDIA);
      }}
      className="w-10 h-10 object-cover rounded-full shadow-sm"
    />
  );
};

type ListParams = { page: number; per_page: number; status: string; search: string; dateRange: string };

const PER_PAGE_KEY = "hc_per_page_requests";
const readPerPage = () => {
  try {
    const n = Number(localStorage.getItem(PER_PAGE_KEY));
    return PER_PAGE_OPTIONS.includes(n) ? n : PER_PAGE_OPTIONS[0];
  } catch {
    return PER_PAGE_OPTIONS[0];
  }
};

const memberName = (r: ConnectionRequestType) =>
  r.user?.full_name || (r.user as { linkedin_full_name?: string } | undefined)?.linkedin_full_name || "A member";

const Request = () => {
  const [pageData, setData] = useState<ConnectionRequestType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [perPage, setPerPage] = useState<number>(readPerPage);
  const [pagePerPage, setPagePerPage] = useState<number | undefined>(undefined);

  // status tab lives in the URL (?status=...) so sidebar submenus can link to it
  const [pageFilter, setPageFilter] = useStatusParam();
  useEffect(() => setCurrentPage(1), [pageFilter]);

  const [searchInput, setSearchInput] = useState("");
  const [searchInputDebounce, setSearchInputDebounce] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [dateError, setDateError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [confirmClose, setConfirmClose] = useState<ConnectionRequestType | null>(null);
  const requestSeq = useRef(0);

  const { isVisible, toggle, visibleCount } = useColumnVisibility("cols_requests_v2", COLUMNS);
  const show = (key: string) => isVisible(key);

  const listParams = useCallback(
    (): ListParams => ({ page: currentPage, per_page: perPage, status: pageFilter, search: searchInput, dateRange }),
    [currentPage, perPage, pageFilter, searchInput, dateRange]
  );

  /** silent: refresh in the background without the loading skeleton */
  const getRequestData = async (params: ListParams, silent = false) => {
    const seq = ++requestSeq.current;
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const res: any = await apiClient.get("admin-get-all-requests", { params });
      if (seq !== requestSeq.current) return;
      const body = res?.data ?? {};
      setData(Array.isArray(body.data) ? body.data : []);
      if (Number.isInteger(body.current_page) && body.current_page !== currentPage) setCurrentPage(body.current_page);
      setLastPage(Number(body.last_page) || 0);
      setTotal(typeof body.total === "number" ? body.total : undefined);
      const used = Number(body.per_page);
      setPagePerPage(Number.isFinite(used) && used > 0 ? used : undefined);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setLoadError(getErrorMessage(error));
      if (!silent) setData([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    getRequestData(listParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, pageFilter, searchInput, dateRange]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInputDebounce]);

  useEffect(() => {
    setDateError("");
    if (startDate && endDate) {
      if (new Date(endDate) >= new Date(startDate)) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setDateError("End date must be on or after the start date.");
      }
    }
    if (!startDate && !endDate) setDateRange("");
  }, [startDate, endDate]);

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const setBusy = (uuid: string, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(uuid);
      else next.delete(uuid);
      return next;
    });

  /** No optimistic change: the row only changes once the server confirms */
  const changeStatus = async (r: ConnectionRequestType, action: "close" | "reopen") => {
    setBusy(r.uuid, true);
    try {
      const res: any = await apiClient.patch(`${action}-request-by-admin/${r.uuid}`);
      const name = memberName(r);
      if (action === "close" && res?.data?.notified === false) {
        notify.warning(`${name}'s request was closed, but they couldn't be notified.`);
      } else {
        notify.success(action === "close" ? `${name}'s request closed and they were notified.` : `${name}'s request reopened.`);
      }
      getRequestData(listParams(), true); // refresh so it moves to the right tab
      return true;
    } catch (error) {
      notify.error(`Couldn't ${action} ${memberName(r)}'s request: ${getErrorMessage(error)}`);
      return false;
    } finally {
      setBusy(r.uuid, false);
    }
  };

  const confirmAndClose = async () => {
    if (!confirmClose) return;
    const ok = await changeStatus(confirmClose, "close");
    if (ok) setConfirmClose(null);
  };

  const changePerPage = (n: number) => {
    try {
      localStorage.setItem(PER_PAGE_KEY, String(n));
    } catch {
      /* not remembered if storage is unavailable */
    }
    setPerPage(n);
    setCurrentPage(1);
  };

  const hasRecords = pageData.length > 0;
  const rowsPerPage = pagePerPage ?? perPage;

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setDates("", "");
    setCurrentPage(1);
  };

  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (dateRange) chips.push({ label: `Date: ${startDate} to ${endDate}`, onRemove: () => setDates("", "") });

  const tabLabel = TABS.find((t) => t.value === pageFilter)?.label;

  return (
    <AdminLayout title="Admin | Connection Requests">
      <PageHeader
        title="Connection Requests"
        subtitle="Requests members have posted"
        actions={
          <ExportButton
            total={total}
            noun="requests"
            disabled={loading}
            onExport={() => downloadCsvExport("requests/export", { ...listParams() }, "connection-requests", total, "requests")}
          />
        }
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              aria-label="Search requests"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by name, username, unique number, location or interest"
            />
          </div>
          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <div className="px-5 mb-4">
          <DateFilter startDate={startDate} endDate={endDate} onChange={setDates} />
          {dateError && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {dateError}
            </p>
          )}
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {loadError && !loading && !hasRecords ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">Couldn't load requests</p>
            <p className="text-sm text-hcMuted mb-4 max-w-md">{loadError}</p>
            <button onClick={() => getRequestData(listParams())} className={btnPrimary}>
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  {show("media") && <th className={th}>Caption</th>}
                  <th className={`${th} left-0 z-20`} style={{ position: "sticky" }}>
                    Full Name
                  </th>
                  {show("username") && <th className={th}>Username</th>}
                  {show("gender") && <th className={th}>Gender</th>}
                  {show("interest") && <th className={th}>Interest</th>}
                  {show("text") && <th className={th}>Prespecified Text</th>}
                  {show("country") && <th className={th}>Country</th>}
                  {show("state") && <th className={th}>State</th>}
                  {show("city") && <th className={th}>City</th>}
                  {show("subscription") && <th className={th}>Subscription</th>}
                  {show("status") && <th className={th}>Status</th>}
                  {show("created") && <th className={th}>Created At</th>}
                  <th className={`${th} text-center`}>Actions</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 2} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {pageData.map((data, i) => {
                    const mediaPath = data.caption_image ?? data.user?.profile_pix_link ?? null;
                    return (
                      <tr key={data.uuid} className="group border-b border-hcBorder hover:bg-hcBg">
                        {show("media") && (
                          <td className={td}>
                            <MediaCell filePath={mediaPath} isGallery={!!data.caption_image} />
                          </td>
                        )}
                        <td className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`} style={{ position: "sticky", left: 0 }}>
                          {memberName(data)}
                        </td>
                        {show("username") && <td className={td}>{data.user?.username ?? "–"}</td>}
                        {show("gender") && <td className={`${td} capitalize`}>{(data.user?.gender ?? "–").toLowerCase()}</td>}
                        {show("interest") && <td className={td}>{data.interest?.interest ?? "–"}</td>}
                        {show("text") && (
                          <td className={`${td} max-w-[260px] truncate`} title={data.prespecified_text?.content}>
                            {data.prespecified_text?.content ?? "–"}
                          </td>
                        )}
                        {show("country") && <td className={td}>{data.country?.name ?? "–"}</td>}
                        {show("state") && <td className={td}>{data.state?.name ?? "–"}</td>}
                        {show("city") && <td className={td}>{data.city?.name ?? "–"}</td>}
                        {show("subscription") && (
                          <td className={td}>
                            {data.user?.active_subscription?.subscription_plan ? (
                              <StatusPill tone="purple" label={data.user.active_subscription.subscription_plan} />
                            ) : (
                              <StatusPill label="None" tone="neutral" />
                            )}
                          </td>
                        )}
                        {show("status") && <td className={td}>{data.status && <StatusPill label={data.status} />}</td>}
                        {show("created") && (
                          <td className={`${td} text-hcMuted`} title={formatDate(data.created_at)}>
                            {formatDate(data.created_at, "date")}
                          </td>
                        )}
                        <td className={`${td} text-center`}>
                          <RequestRowActions
                            status={data.status ?? ""}
                            busy={busyIds.has(data.uuid)}
                            openUp={pageData.length > 3 && i >= pageData.length - 2}
                            onClose={() => setConfirmClose(data)}
                            onReopen={() => changeStatus(data, "reopen")}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title={
                  chips.length
                    ? "No requests match these filters"
                    : pageFilter
                    ? `No ${(tabLabel ?? pageFilter).toLowerCase()} requests`
                    : "No requests yet"
                }
                message={
                  chips.length
                    ? "Try changing your search or filters."
                    : pageFilter
                    ? "There are no requests with this status right now."
                    : "Requests will appear here when members post them."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
            perPage={perPage}
            pageSize={rowsPerPage}
            onPerPageChange={changePerPage}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!confirmClose}
        title={`Close ${confirmClose ? memberName(confirmClose) : "this member"}'s request?`}
        message="Use this for requests that break the media guidelines. The member will be notified that their request was removed and can post a new one. You can reopen it later if needed."
        confirmLabel="Close request"
        loading={!!confirmClose && busyIds.has(confirmClose.uuid)}
        onConfirm={confirmAndClose}
        onCancel={() => setConfirmClose(null)}
      />
    </AdminLayout>
  );
};

export default Request;
